import React, { useState, useEffect, useRef } from 'react'
import imageUrlBuilder from '@sanity/image-url'
import Select from '@mui/material/Select'
import MenuItem from '@mui/material/MenuItem'
import FormControl from '@mui/material/FormControl'
import InputLabel from '@mui/material/InputLabel'
import _ from 'lodash'
// @ts-expect-error
import sanityClient from '@sanity/client'

import './care-team-grid.css'
import { CareTeamProps } from './CareTeam'
import { getVisibleSpecialties } from './utils'
import { getWidgetUrl } from '../joinWidget'

const client = sanityClient({
  projectId: process.env.GATSBY_SANITY_PROJECT_ID,
  dataset: process.env.GATSBY_SANITY_DATASET,
  useCdn: false,
})

const CareTeamGrid: React.FC<{ data: CareTeamProps }> = ({ data }) => {
  const { itemsPerRow = 3, showSpecialtyFilter, backgroundColor } = data
  const builder = imageUrlBuilder(client)

  const [allProviderTypes, setAllProviderTypes] = useState<string[]>(['all'])
  const [allLocations, setAllLocations] = useState<string[]>(['all'])
  const [filteredSpecialties, setFilteredSpecialties] = useState<string[]>([
    'all',
  ])
  const [selectedFilters, setSelectedFilters] = useState({
    specialties: 'all',
    providerType: 'all',
    location: 'all',
  })
  const [filteredProviders, setFilteredProviders] = useState(
    data?.providers || []
  )
  const [visibleCount, setVisibleCount] = useState(
    data?.visibleCount || itemsPerRow * 2
  )

  const widgetURL = getWidgetUrl()

  const specialtiesContainerRef = useRef<HTMLDivElement>(null)

  const getImageUrl = (asset: any) => {
    if (!asset) return ''
    return builder.image(asset).url()
  }

  // this add the unique values to the provider types, locations and specialties
  useEffect(() => {
    const uniqueSpecialties = new Set<string>()
    const uniqueProviderTypes = new Set<string>()
    const uniqueLocations = new Set<string>()

    data?.providers?.forEach((provider: any) => {
      provider?.content?.main?.specialties?.forEach((specialty: string) => {
        uniqueSpecialties.add(specialty?.toLocaleLowerCase())
      })
      if (provider?.content?.main?.team) {
        uniqueProviderTypes.add(provider.content.main.team.toLocaleLowerCase())
      }
      provider?.content?.main?.locations?.forEach((location: any) => {
        if (location?.content?.main?.title) {
          uniqueLocations.add(location.content.main.title.toLocaleLowerCase())
        }
      })
    })

    setAllProviderTypes(['all', ...Array.from(uniqueProviderTypes)])
    setAllLocations(['all', ...Array.from(uniqueLocations)])
    setFilteredSpecialties(['all', ...Array.from(uniqueSpecialties)])
  }, [data?.providers])

  // this filter the providers based on the selected filters
  useEffect(() => {
    const filtered = data?.providers?.filter((provider: any) => {
      const matchesSpecialty =
        selectedFilters.specialties === 'all' ||
        provider?.content?.main?.specialties?.some((specialty: string) =>
          selectedFilters.specialties.includes(specialty?.toLocaleLowerCase())
        )

      const matchesProviderType =
        selectedFilters.providerType === 'all' ||
        provider?.content?.main?.team?.toLocaleLowerCase() ===
          selectedFilters.providerType

      const matchesLocation =
        selectedFilters.location === 'all' ||
        provider?.content?.main?.locations?.some(
          (location: any) =>
            location?.content?.main?.title?.toLocaleLowerCase() ===
            selectedFilters.location
        )

      return matchesSpecialty && matchesProviderType && matchesLocation
    })

    setFilteredProviders(filtered || [])

    const updatedSpecialties = new Set<string>()
    filtered?.forEach((provider: any) => {
      provider?.content?.main?.specialties?.forEach((specialty: string) => {
        updatedSpecialties.add(specialty?.toLocaleLowerCase())
      })
    })

    setFilteredSpecialties(['all', ...Array.from(updatedSpecialties)])
  }, [selectedFilters, data?.providers])

  const handleFilterChange = (filterType: string, value: string) => {
    setSelectedFilters(prev => ({
      ...prev,
      [filterType]: value,
    }))
  }

  const handleShowMore = () => {
    if (visibleCount >= filteredProviders.length) {
      setVisibleCount(itemsPerRow * 2)
    } else {
      setVisibleCount(prev =>
        Math.min(prev + itemsPerRow, filteredProviders.length)
      )
    }
  }

  const handleShowLess = () => {
    setVisibleCount(data?.visibleCount || itemsPerRow * 2)
    const firstCard = document.querySelector('.care-team-grid__card_item')
    if (firstCard) {
      firstCard.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }
  }

  return (
    <div className={`care-team-grid__background  ${backgroundColor}`}>
      <div className={`care-team-grid__container`}>
        <span className="care-team-grid__title">{data?.title}</span>

        <span className="care-team-grid__subtitle">{data?.subtitle}</span>

        <div className="care-team-grid__filter_container">
          <FormControl>
            <InputLabel id="care-team-grid__provider_type_filter__label">
              Provider Type
            </InputLabel>
            <Select
              labelId="care-team-grid__provider_type_filter__label"
              id="care-team-grid__provider_type_filter"
              label="Provider Type"
              value={selectedFilters.providerType}
              onChange={e => handleFilterChange('providerType', e.target.value)}
              variant="outlined"
            >
              {allProviderTypes.map((type, index) => (
                <MenuItem key={index} value={type}>
                  {type === 'all' ? 'All' : _.startCase(_.toLower(type))}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl>
            <InputLabel id="care-team-grid__location_filter__label">
              Location
            </InputLabel>
            <Select
              labelId="care-team-grid__location_filter__label"
              id="care-team-grid__location_filter"
              label="Location"
              value={selectedFilters.location}
              onChange={e => handleFilterChange('location', e.target.value)}
              variant="outlined"
            >
              {allLocations.map((location, index) => (
                <MenuItem key={index} value={location}>
                  {location === 'all'
                    ? 'All'
                    : _.startCase(_.toLower(location))}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {showSpecialtyFilter && (
            <FormControl>
              <InputLabel id="care-team-grid__specialties_filter__label">
                Specialties
              </InputLabel>
              <Select
                labelId="care-team-grid__specialties_filter__label"
                id="care-team-grid__specialties_filter"
                label="Specialties"
                value={selectedFilters.specialties}
                onChange={e =>
                  handleFilterChange('specialties', e.target.value)
                }
                variant="outlined"
              >
                {filteredSpecialties.map((specialty, index) => (
                  <MenuItem key={index} value={specialty}>
                    {specialty === 'all'
                      ? 'All'
                      : _.startCase(_.toLower(specialty))}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        </div>

        <div
          className="care-team-grid__cards_container"
          style={{ gridTemplateColumns: `repeat(${itemsPerRow}, 1fr)` }}
        >
          {filteredProviders.slice(0, visibleCount).map((provider: any) => {
            const main = provider?.content?.main
            const slug = main?.slug?.current
            const providerURL = `/team/${slug}`
            const imageUrl = getImageUrl(main?.image?.asset)

            const { visible, remainingCount } = getVisibleSpecialties(
              main?.specialties || [],
              specialtiesContainerRef,
              'grid'
            )
            return (
              <div className="care-team-grid__card_item" key={provider?._key}>
                <div className="care-team-grid__provider_details">
                  <div className="care-team-grid__provider_image_container">
                    <img
                      className="care-team-grid__provider_image"
                      src={imageUrl || ''}
                      alt="Provider Image"
                      style={{
                        borderRadius: '1rem',
                      }}
                    />
                  </div>
                  <div className="care-team-grid__provider_meta">
                    <div className="care-team-grid__provider_title">
                      {main?.title}
                    </div>
                    <div
                      className="care-team-grid__specialties_container"
                      ref={specialtiesContainerRef}
                    >
                      {visible.map((specialty: any, index: number) => (
                        <div key={index} className="care-team-grid__specialty">
                          {specialty}
                        </div>
                      ))}
                      {remainingCount > 0 && (
                        <div className="care-team-grid__specialty">
                          +{remainingCount}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                <div className="care-team-grid__actions">
                  <button
                    type="button"
                    className="care-team-grid__appointment_button"
                    onClick={() => {
                      window.open(`${widgetURL}`, '_blank')
                    }}
                  >
                    Make an appointment
                  </button>
                  <a
                    className="care-team-grid__learn_more"
                    target="__blank"
                    href={providerURL}
                  >
                    Learn More
                  </a>
                </div>
              </div>
            )
          })}
        </div>
        <div className="care-team-grid__info_actions_container">
          <span>
            You’re viewing {Math.min(visibleCount, filteredProviders.length)} of{' '}
            {filteredProviders.length} providers
          </span>
          {visibleCount < filteredProviders.length ? (
            <button
              type="button"
              className="care-team-grid__show_more_button"
              onClick={handleShowMore}
            >
              Show More
            </button>
          ) : (
            <button
              type="button"
              className="care-team-grid__show_more_button"
              onClick={handleShowLess}
            >
              Show Less
            </button>
          )}
        </div>
      </div>
    </div>
  )
}

export { CareTeamGrid }
export type { CareTeamProps }
